import { useState } from 'react';
import { useTimer } from '../contexts/TimerContext';
import { useScreenCapture } from '../contexts/ScreenCaptureContext';
import { useSubscription } from './SubscriptionGuard';
import { Camera, CameraOff, Loader2, Monitor, AlertCircle, Shield, Eye, Clock, Trash2, Info } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';

export const ScreenCaptureSection = () => {
  const { isRunning, isPaused } = useTimer();
  const {
    isEnabled,
    isCapturing,
    lastCapture,
    captureCount,
    hasPermission,
    captureFrequency,
    error,
    needsReconnect,
    requestScreenPermission,
    reconnectCapture,
    stopScreenSharing,
    captureScreenshot
  } = useScreenCapture();
  const { subscription } = useSubscription() || {};
  const isFreePlan = subscription?.plan === 'free';
  
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  // Handle enable button click - show consent modal first
  const handleEnableClick = () => {
    if (isEnabled && hasPermission) {
      stopScreenSharing();
      toast.success('Screen capture disabled');
    } else {
      setShowConsentModal(true);
      setConsentChecked(false);
    }
  };

  // Handle consent confirmation
  const handleConsentConfirm = async () => {
    setShowConsentModal(false);
    await requestScreenPermission();
  };

  // Manual capture
  const handleManualCapture = () => {
    if (hasPermission && !isPaused) {
      captureScreenshot();
    } else if (isPaused) {
      toast.error('Cannot capture while timer is paused');
    } else {
      toast.error('Please enable screen sharing first');
    }
  };

  return (
    <>
      {/* Privacy Consent Modal */}
      <Dialog open={showConsentModal} onOpenChange={setShowConsentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              Screen Capture Privacy Notice
            </DialogTitle>
            <DialogDescription>
              Please review the following information before enabling screen capture.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <Monitor className="w-5 h-5 text-slate-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">What is captured</p>
                <p className="text-xs text-slate-500">
                  {isFreePlan 
                    ? 'Screenshots of your selected screen or window to track your work activity' 
                    : 'Screenshots of your selected screen or window during active time tracking'}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <Clock className="w-5 h-5 text-slate-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Capture Frequency</p>
                <p className="text-xs text-slate-500">Every {captureFrequency} minute{captureFrequency > 1 ? 's' : ''} while timer is running (not during pauses)</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <Eye className="w-5 h-5 text-slate-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Who can view</p>
                <p className="text-xs text-slate-500">
                  {isFreePlan 
                    ? 'Only you can view these screenshots. You may export them as proof of work for clients' 
                    : 'Only your Manager and Admin can view these screenshots'}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <Trash2 className="w-5 h-5 text-slate-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Data Retention</p>
                <p className="text-xs text-slate-500">Screenshots are retained for 30 days, then automatically deleted</p>
              </div>
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="consent"
                checked={consentChecked}
                onCheckedChange={setConsentChecked}
              />
              <label htmlFor="consent" className="text-sm cursor-pointer">
                {isFreePlan 
                  ? 'I understand and consent to screen capture to track my work activity'
                  : 'I understand and consent to screen capture during my tracked work time'}
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConsentModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConsentConfirm}
              disabled={!consentChecked}
              className="bg-blue-600 dark:bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-500 text-white"
            >
              I Understand & Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Screen Capture Section UI - Collapsed when idle */}
      {!isRunning ? (
        // Collapsed state - single slim bar
        <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
          <Camera className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-500">Screen Capture — will activate when timer starts</span>
        </div>
      ) : (
        // Expanded state when timer is running
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Screen Capture</span>
              {isEnabled && hasPermission && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">
                  Active
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isEnabled && hasPermission ? (
                <>
                  <span className="text-xs text-slate-500">
                    {captureCount} capture{captureCount !== 1 ? 's' : ''}
                    {lastCapture && ` · Last: ${lastCapture.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                  </span>
                  {isCapturing && <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />}
                  <Button
                    onClick={handleManualCapture}
                    disabled={isCapturing || isPaused}
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    data-testid="manual-capture-btn"
                  >
                    <Camera className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    onClick={handleEnableClick}
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                  >
                    <CameraOff className="w-3.5 h-3.5 mr-1" />
                    Disable
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleEnableClick}
                  size="sm"
                  className="h-7 text-xs bg-blue-600 dark:bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-500 text-white"
                  data-testid="screen-capture-toggle-btn"
                >
                  <Camera className="w-3.5 h-3.5 mr-1" />
                  Enable Capture
                </Button>
              )}
            </div>
          </div>
          
          {/* Reconnect banner after page refresh */}
          {needsReconnect && !hasPermission && (
            <div className="flex items-center justify-between mt-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/20" data-testid="screen-capture-reconnect-banner">
              <div className="flex items-center gap-2">
                <CameraOff className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Screen capture disconnected</span>
              </div>
              <Button
                onClick={reconnectCapture}
                size="sm"
                className="h-6 px-3 text-xs bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-600 text-white rounded-full"
                data-testid="reconnect-capture-btn"
              >
                Click to reconnect
              </Button>
            </div>
          )}

          {/* Error message if any */}
          {error && (
            <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/10 text-red-600 text-xs">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>{error}</span>
            </div>
          )}
          
          {/* Paused indicator */}
          {isPaused && isEnabled && hasPermission && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-600 dark:text-amber-400">
              <Info className="w-3 h-3" />
              <span>Capture paused (timer paused)</span>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ScreenCaptureSection;
